import {
  formatJapaneseDate,
  formatJapaneseYearMonth,
  formatJstDateTime,
} from "@milledon/lib";

const currencyFormatter = new Intl.NumberFormat("ja-JP", {
  style: "currency",
  currency: "JPY",
});

const numberFormatter = new Intl.NumberFormat("ja-JP");

export type DateDisplayProps = Readonly<{
  children: string;
  title?: string;
}>;

export function formatDateTime(value: string | null): string {
  return formatDateValue(value, formatJstDateTime);
}

export function formatCurrency(value: number): string {
  return currencyFormatter.format(value);
}

export function formatDate(value: string | null): string {
  return formatDateValue(value, formatJapaneseDate);
}

export function formatMonth(value: string | null): string {
  return formatDateValue(value, formatJapaneseYearMonth);
}

export function dateDisplayProps(
  value: string | null,
  formatter: (input: string | null) => string,
): DateDisplayProps {
  const children = formatter(value);
  return value && children === "—"
    ? { children, title: value }
    : { children };
}

function formatDateValue(
  value: string | null,
  formatter: (input: string) => string,
): string {
  if (!value) return "—";

  try {
    return formatter(value);
  } catch {
    return "—";
  }
}

export function formatNumber(value: number): string {
  return numberFormatter.format(value);
}

export function formatGrams(value: number): string {
  return `${formatNumber(value)} g`;
}

export function shortId(value: string): string {
  return value.length > 10 ? `${value.slice(0, 8)}…` : value;
}

export function isPast(value: string): boolean {
  const timestamp = Date.parse(value);
  return !Number.isNaN(timestamp) && timestamp < Date.now();
}

const orderStatusLabels: Record<string, string> = {
  reserved: "予約",
  allocated: "割当済み",
  canceled: "キャンセル",
  returned: "返品",
};

const billingStatusLabels: Record<string, string> = {
  charged: "課金済み",
  refunded: "返金済み",
};

const holdStatusLabels: Record<string, string> = {
  holding: "保持中",
  payment_in_progress: "決済中",
  confirmed: "確定",
  released: "解放",
  expired: "期限切れ",
};

const mailStatusLabels: Record<string, string> = {
  pending: "待機",
  sending: "送信中",
  sent: "送信済み",
  suppressed: "抑止",
  dead: "停止（dead）",
};

const varietyLabels: Record<string, string> = {
  hitomebore: "ひとめぼれ",
  sasanishiki: "ササニシキ",
  tsuyahime: "つや姫",
};

const millingTypeLabels: Record<string, string> = {
  brown: "玄米",
  white: "白米",
  rinse_free: "無洗米",
};

const kindLabels: Record<string, string> = {
  trial1000: "お試し ¥1,000",
  addon: "追加購入",
};

export function formatOrderStatus(value: string): string {
  return orderStatusLabels[value] ?? value;
}

export function formatBillingStatus(value: string): string {
  return billingStatusLabels[value] ?? value;
}

export function formatHoldStatus(value: string): string {
  return holdStatusLabels[value] ?? value;
}

export function formatMailStatus(value: string): string {
  return mailStatusLabels[value] ?? value;
}

export function formatVariety(value: string): string {
  return varietyLabels[value] ?? value;
}

export function formatMillingType(value: string): string {
  return millingTypeLabels[value] ?? value;
}

export function formatKind(value: string): string {
  return kindLabels[value] ?? value;
}

export interface MailTargetSource {
  oneTimeOrderId: string | null;
  cycleId: string | null;
  shipmentId: string | null;
  subscriptionId: string | null;
}

export interface MailTarget {
  kind: string;
  id: string;
}

// mail_outbox の型付き FK は高々1つが非 NULL（DB の CHECK が保証）。
// 表示は最初に見つかった1件のみ。
export function mailTarget(mail: MailTargetSource): MailTarget | null {
  if (mail.oneTimeOrderId) {
    return { kind: "単品注文", id: mail.oneTimeOrderId };
  }
  if (mail.cycleId) {
    return { kind: "お届け回", id: mail.cycleId };
  }
  if (mail.shipmentId) {
    return { kind: "発送", id: mail.shipmentId };
  }
  if (mail.subscriptionId) {
    return { kind: "定期契約", id: mail.subscriptionId };
  }

  return null;
}

export function formatMailTarget(mail: MailTargetSource): string {
  const target = mailTarget(mail);
  return target ? `${target.kind} ${shortId(target.id)}` : "—";
}
